import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { PartyRoleAssignmentService } from '../../services/party-role-assignment.service';
import { EntityService } from '../../services/entity.service';
import { PartyRoleAssignment } from '../../models/party-role-assignment.model';
import { LoggerService } from '../../services/logger.service';

@Component({
  selector: 'app-user-role-manager',
  templateUrl: './user-role-manager.component.html',
  styleUrls: ['./user-role-manager.component.scss']
})
export class UserRoleManagerComponent implements OnChanges {
  @Input() userId!: string;

  assignments: PartyRoleAssignment[] = [];
  roles: any[] = [];
  
  // Almacenes de datos completos (para no saturar el backend con peticiones)
  allClients: any[] = [];
  allSocieties: any[] = [];
  allDepartments: any[] = [];
  allProjects: any[] = [];

  // Listas filtradas que se mostrarán en los combos
  filteredSocieties: any[] = [];
  filteredDepartments: any[] = [];
  filteredProjects: any[] = [];

  // Variables del formulario
  selectedRoleId: string = '';
  selectedContextType: string = 'global';
  
  // IDs seleccionados en la cascada
  selectedClientId: string = '';
  selectedSocietyId: string = '';
  selectedDepartmentId: string = '';
  selectedProjectId: string = '';

  contextTypes = [
    { id: 'global', name: 'Global (Todo el sistema)' },
    { id: 'client', name: 'Cliente' },
    { id: 'society', name: 'Sociedad' },
    { id: 'department', name: 'Departamento' },
    { id: 'project', name: 'Proyecto' }
  ];

  constructor(
    private roleAssignmentService: PartyRoleAssignmentService,
    private entityService: EntityService,
    private logger: LoggerService
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['userId'] && this.userId) {
      this.loadAssignments();
      this.loadRoles();
      this.loadAllContexts(); // Precargamos los catálogos
    }
  }

  loadAssignments() {
    this.roleAssignmentService.getUserAssignments(this.userId).subscribe(
      (data) => this.assignments = data,
      (error) => this.logger.error('Error cargando roles del usuario', error)
    );
  }

  loadRoles() {
    this.entityService.getAll('role').subscribe(
      (data) => this.roles = data,
      (error) => this.logger.error('Error cargando catálogo de roles', error)
    );
  }

  // Precarga todos los datos de contexto una sola vez para hacer filtros locales rápidos
  loadAllContexts() {
    this.entityService.getAll('client').subscribe(d => this.allClients = d);
    this.entityService.getAll('society').subscribe(d => this.allSocieties = d);
    this.entityService.getAll('department').subscribe(d => this.allDepartments = d);
    this.entityService.getAll('project').subscribe(d => this.allProjects = d);
  }

  // LÓGICA DE CASCADA --------------------------------------------------------

  onContextTypeChange() {
    this.resetCascade();
  }

  onClientChange() {
    this.selectedSocietyId = '';
    this.selectedDepartmentId = '';
    this.selectedProjectId = '';
    // Filtramos sociedades basándonos en el clientid seleccionado
    this.filteredSocieties = this.allSocieties.filter(s => s.clientid === this.selectedClientId);
  }

  onSocietyChange() {
    this.selectedDepartmentId = '';
    this.selectedProjectId = '';
    // Filtramos departamentos basándonos en el societyid seleccionado
    this.filteredDepartments = this.allDepartments.filter(d => d.societyid === this.selectedSocietyId);
  }

  onDepartmentChange() {
    this.selectedProjectId = '';
    // Filtramos proyectos basándonos en el departmentid seleccionado
    this.filteredProjects = this.allProjects.filter(p => p.departmentid === this.selectedDepartmentId);
  }

  // GETTERS PARA CONTROLAR LA VISIBILIDAD DE LOS COMBOS EN EL HTML ----------

  get showClient(): boolean { 
    return this.selectedContextType !== 'global'; 
  }
  get showSociety(): boolean { 
    return ['society', 'department', 'project'].includes(this.selectedContextType); 
  }
  get showDepartment(): boolean { 
    return ['department', 'project'].includes(this.selectedContextType); 
  }
  get showProject(): boolean { 
    return this.selectedContextType === 'project'; 
  }

  // VALIDACIÓN DEL FORMULARIO -----------------------------------------------

  isFormValid(): boolean {
    if (!this.selectedRoleId) return false;
    
    switch (this.selectedContextType) {
      case 'global': return true;
      case 'client': return !!this.selectedClientId;
      case 'society': return !!this.selectedSocietyId;
      case 'department': return !!this.selectedDepartmentId;
      case 'project': return !!this.selectedProjectId;
      default: return false;
    }
  }

  // ASIGNACIÓN Y RESETEO ----------------------------------------------------

  assignRole() {
    if (!this.isFormValid()) return;

    const payload: PartyRoleAssignment = {
      userid: this.userId,
      roleid: this.selectedRoleId
    };

    // Solo enviamos al backend el ID del nivel más bajo que el usuario quería configurar
    if (this.selectedContextType === 'client') payload.clientid = this.selectedClientId;
    if (this.selectedContextType === 'society') payload.societyid = this.selectedSocietyId;
    if (this.selectedContextType === 'department') payload.departmentid = this.selectedDepartmentId;
    if (this.selectedContextType === 'project') payload.projectid = this.selectedProjectId;

    this.roleAssignmentService.assignRole(payload).subscribe(
      () => {
        this.logger.log('Rol asignado correctamente');
        this.loadAssignments();
        this.resetForm();
      },
      (error) => this.logger.error('Error al asignar el rol', error)
    );
  }

  revokeRole(assignmentId: string) {
    if (confirm('¿Estás seguro de revocar este rol al usuario? Se desconectará de sus sesiones activas.')) {
      this.roleAssignmentService.revokeRole(assignmentId).subscribe(
        () => this.loadAssignments(),
        (error) => this.logger.error('Error al revocar el rol', error)
      );
    }
  }

  resetForm() {
    this.selectedRoleId = '';
    this.selectedContextType = 'global';
    this.resetCascade();
  }

  resetCascade() {
    this.selectedClientId = '';
    this.selectedSocietyId = '';
    this.selectedDepartmentId = '';
    this.selectedProjectId = '';
    this.filteredSocieties = [];
    this.filteredDepartments = [];
    this.filteredProjects = [];
  }

  getContextLabel(assignment: PartyRoleAssignment): string {
    // Busca el nombre real en nuestros arrays pre-cargados
    if (assignment.projectid) {
      const p = this.allProjects.find(x => x.id === assignment.projectid);
      return `Proyecto: ${p ? (p.name || p.description) : assignment.projectid.substring(0,8)}`;
    }
    if (assignment.departmentid) {
      const d = this.allDepartments.find(x => x.id === assignment.departmentid);
      return `Departamento: ${d ? (d.name || d.description) : assignment.departmentid.substring(0,8)}`;
    }
    if (assignment.societyid) {
      const s = this.allSocieties.find(x => x.id === assignment.societyid);
      return `Sociedad: ${s ? (s.name || s.description) : assignment.societyid.substring(0,8)}`;
    }
    if (assignment.clientid) {
      const c = this.allClients.find(x => x.id === assignment.clientid);
      return `Cliente: ${c ? (c.name || c.description) : assignment.clientid.substring(0,8)}`;
    }
    return 'Global';
  }
}